/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { companiesAPI } from '../../services/api';

// COMPANY-001 / COMPANY-004: The single company selector used everywhere a
// property or expense is assigned to a company.
//
// It self-loads the company list on every mount, deliberately. An earlier
// attempt read the list from AppDataContext instead, which is fetched once
// at app start — so a company created in Administration did not appear in
// the property form until a full page reload. Two companies existed, the
// context still held zero, and the picker silently rendered nothing. A tiny
// extra fetch is worth more than that failure mode.
//
// Behaviour:
//   ownership is not 'company'  -> renders nothing, clears any stale value
//   0 companies                 -> prompt to create one (no silent auto-create)
//   exactly 1 company           -> auto-assigned, shown as text + "Change"
//   2+ companies                -> dropdown, plus "+ Add New Company"
//
// If `ownership` is omitted the component always shows the dropdown, so
// existing callers that have no ownership concept (Company Expenses,
// Transactions) keep working unchanged.
export default function CompanyPicker({ ownership, value, onChange, style, allowCreate = true }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCompany, setNewCompany] = useState({ name:'', registration_number:'', registered_address:'', jurisdiction:'UK', currency:'GBP' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false); // reveal the picker when only one company exists

  // onChange is usually an inline arrow from the parent, so it is a new
  // function on every render. Holding it in a ref keeps it out of effect
  // dependency arrays and stops the auto-assign effect re-firing endlessly.
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  const load = () => {
    setLoading(true);
    companiesAPI.getAll()
      .then(rows => { setCompanies(Array.isArray(rows) ? rows : []); setLoading(false); })
      .catch(() => { setCompanies([]); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const active = companies.filter(c => !(c.archived === true || c.archived === 'true'));
  // Only apply the ownership rule when the caller actually passes ownership.
  const ownershipKnown = ownership !== undefined && ownership !== null;
  const isCompanyOwned = ownershipKnown ? String(ownership).toLowerCase() === 'company' : true;

  // COMPANY-003: keep the parent's value consistent with the rules. A
  // personally- or jointly-owned property must never carry a company_id --
  // `ownership` is the sole source of truth for S24 treatment, and a stray
  // company_id would sweep it into the company tax bucket.
  useEffect(() => {
    if (loading) return;
    if (!isCompanyOwned) {
      if (value) onChangeRef.current(null);
      return;
    }
    if (active.length === 1 && !value) onChangeRef.current(active[0].id);
  }, [loading, isCompanyOwned, active.length, value]);

  if (!isCompanyOwned) return null;

  const selectEl = (
    <select
      value={value || ''}
      onChange={(e) => {
        if (e.target.value === '__add_new__') { setShowAddModal(true); return; }
        // Coerce to an integer -- this previously emitted the raw string from
        // the <option>, so companyId arrived as "3" rather than 3 and strict
        // comparisons against numeric ids silently failed.
        onChange(e.target.value ? parseInt(e.target.value, 10) : null);
      }}
      disabled={loading}
      style={style || { width:'100%', padding:'0.5rem', border:'1px solid var(--border)', borderRadius:'0.375rem' }}
    >
      {/* COMPANY-003: previously read "Personal / No Company", which invited
          someone to mark a company-owned property as personal from a field
          that has no bearing on ownership. This only picks WHICH company;
          ownership is set by the Ownership field above. */}
      <option value="">- Select a company -</option>
      {active.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      {allowCreate && <option value="__add_new__">+ Add New Company...</option>}
    </select>
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    // COMPANY-004 BUG FIX: this modal contains its own <form>, and it renders
    // from inside other screens' forms. React builds the DOM via appendChild,
    // so the nested form genuinely exists, submit events bubble, and React
    // propagates them up the React tree -- meaning the PARENT form's onSubmit
    // fired on this same click. On Add Property that ran handleAdd
    // concurrently: the modal closed and reset mid-request, the company was
    // never attached, and everything typed was discarded.
    // preventDefault alone does not stop propagation. This does.
    e.stopPropagation();
    if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
      e.nativeEvent.stopImmediatePropagation();
    }
    setError('');
    if (!newCompany.name.trim()) { setError('Company name is required'); return; }
    setSaving(true);
    try {
      const created = await companiesAPI.create(newCompany);
      setCompanies(prev => [...prev, created].sort((a,b) => (a.name||'').localeCompare(b.name||'')));
      onChange(created.id);
      setExpanded(true);
      setShowAddModal(false);
      setNewCompany({ name:'', registration_number:'', registered_address:'', jurisdiction:'UK', currency:'GBP' });
    } catch (err) { setError(err.error || 'Failed to create company'); }
    finally { setSaving(false); }
  };

  return (
    <>
      {loading ? (
        <div style={{ fontSize:'0.8rem', color:'var(--text-secondary)' }}>Loading companies...</div>
      ) : active.length === 0 ? (
        // COMPANY-003c: no silent auto-create. A company record carries a
        // registration number, jurisdiction and tax year end that drive the
        // CT600 reporting period -- those must be chosen, not invented.
        <div style={{ fontSize:'0.8rem', color:'var(--text-secondary)' }}>
          No companies set up yet.{' '}
          {allowCreate && (
            <button type="button" onClick={() => setShowAddModal(true)}
              style={{ background:'none', border:'none', color:'var(--brand-green)', textDecoration:'underline', cursor:'pointer', padding:0, fontSize:'0.8rem' }}>
              Add a company
            </button>
          )}
          <div style={{ marginTop:'0.25rem' }}>You can save this property now and assign it later.</div>
        </div>
      ) : active.length === 1 && !expanded ? (
        // Single company: assigned automatically, no dropdown to click
        // through. The "Change" link keeps the capability discoverable rather
        // than hiding it entirely -- needed the moment someone incorporates a
        // second company.
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap' }}>
          <span style={{ fontSize:'0.875rem', fontWeight:600 }}>{active[0].name}</span>
          <button type="button" onClick={() => setExpanded(true)}
            style={{ background:'none', border:'none', color:'var(--brand-green)', textDecoration:'underline', cursor:'pointer', padding:0, fontSize:'0.75rem' }}>
            Change
          </button>
        </div>
      ) : selectEl}

      {/* COMPANY-004: rendered through a portal to document.body so the modal
          -- and its <form> -- sit structurally OUTSIDE whatever form this
          picker was dropped into. stopPropagation above is the belt; this is
          the braces. */}
      {showAddModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <span className="modal-title">Add New Company</span>
              <button type="button" className="modal-close" onClick={() => setShowAddModal(false)}>&times;</button>
            </div>
            {error && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{error}</div>}
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Company Name *</label>
                <input type="text" required value={newCompany.name} onChange={e => setNewCompany(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Oakfield Properties Ltd" autoFocus />
              </div>
              <div className="form-group">
                <label>Company Number</label>
                <input type="text" value={newCompany.registration_number} onChange={e => setNewCompany(p => ({ ...p, registration_number: e.target.value }))} placeholder="Optional" />
              </div>
              <div className="form-group">
                <label>Registered Address</label>
                <input type="text" value={newCompany.registered_address} onChange={e => setNewCompany(p => ({ ...p, registered_address: e.target.value }))} placeholder="Optional" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div className="form-group">
                  <label>Jurisdiction</label>
                  <select value={newCompany.jurisdiction} onChange={e => setNewCompany(p => ({ ...p, jurisdiction: e.target.value }))}>
                    <option value="UK">UK</option>
                    <option value="Ireland">Ireland</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Currency</label>
                  <select value={newCompany.currency} onChange={e => setNewCompany(p => ({ ...p, currency: e.target.value }))}>
                    <option value="GBP">GBP</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
              <p style={{ fontSize:'0.72rem', color:'var(--text-secondary)', margin:'0.5rem 0 0' }}>
                Accounting year end and VAT status are set in Administration &rarr; Companies.
                They default to 1 October and not registered until you change them, and they
                determine the reporting period on company tax reports.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Creating...' : 'Create Company'}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
